import styled from 'styled-components';
import { Send, Visibility } from '@material-ui/icons';

export const Header = styled.div`
  background-color: #005092;
  color: white;
  padding: 10px;
  font-size: 18px;
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

export const Line = styled.div`
  min-width: 120px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 40vw;
  min-width: 500px;
  height: 100%;
`;

export const Content = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  overflow: auto;
`;

export const Title = styled.div`
  font-size: 16px;
  display: flex;
  gap: 10px;
  color: #007ade;
  font-weight: bold;
`;

export const Bold = styled.div`
  font-size: 16px;
  display: flex;
  justify-content: center;
  font-weight: bold;
`;

export const ChatHistory = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: auto;
  height: calc(100% - 130px);
  margin: 15px 0 15px 15px;
  padding-right: 30px;
`;

export const LeftSender = styled.div`
  color: #0176ff;
  font-size: 12px;
  font-weight: bold;
  margin-right: auto;
`;

export const LeftChatMessage = styled.div`
  background: #f7f7f7;
  width: 80%;
  padding: 10px;
  border-radius: 5px;
  margin-right: auto;
  margin-bottom: 15px;
  white-space: pre-line;
`;

// MMS media in the thread. Inbound sits on the left like a contact's message;
// outbound sits on the right like the org's, which is the only difference.
export const MediaContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 80%;
  margin-bottom: 15px;
  ${({ outbound }) =>
    outbound
      ? `margin-left: auto;
         margin-right: 0;
         justify-content: flex-end;`
      : 'margin-right: auto;'}
`;

export const MediaThumbnail = styled.img`
  max-width: 180px;
  max-height: 180px;
  border-radius: 5px;
  border: 1px solid #e3e3e3;
  cursor: pointer;
  object-fit: cover;
`;

// Wraps a single media item (thumbnail or file chip) with its download control.
export const MediaItemWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

// Small "Download" link shown under each stored media item.
export const MediaDownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #0176ff;
  font-size: 11px;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  svg {
    font-size: 14px;
  }
`;

// Clickable chip for non-image files (e.g. PDFs). Opens the original in a new tab.
export const MediaFileChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 220px;
  padding: 10px 14px;
  border-radius: 5px;
  border: 1px solid #e3e3e3;
  background: #f7f7f7;
  color: #565656;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: #f2f9ff;
    border-color: #0176ff;
  }

  svg {
    font-size: 20px;
    color: #d0021b;
    flex-shrink: 0;
  }
`;

// The chip's label. Outbound media is labelled with the sender's filename, which
// has no useful upper bound, so it ellipsizes inside the chip's max-width rather
// than stretching it. `min-width: 0` is what lets a flex child shrink below its
// content — without it `text-overflow` never engages.
export const MediaFileName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

// ---------------------------------------------------------------------------
// Composer attachment (outbound media). See `add-thub-outbound-media`.
// ---------------------------------------------------------------------------

// Sits between the "Reply" header and the textarea, so the attachment is visible
// without pushing the send controls off the bottom of the drawer.
export const AttachmentBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
`;

export const AttachmentPreview = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  align-self: flex-start;
  max-width: 100%;
  padding: 8px 10px;
  border-radius: 5px;
  border: 1px solid #e3e3e3;
  background: #f7f7f7;
`;

export const AttachmentThumbnail = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 4px;
  border: 1px solid #e3e3e3;
  object-fit: cover;
  flex-shrink: 0;
`;

export const AttachmentName = styled.span`
  font-size: 12px;
  color: #565656;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
`;

export const AttachmentMeta = styled.span`
  font-size: 11px;
  color: #8b8b8b;
  flex-shrink: 0;
`;

export const AttachmentNotice = styled.div`
  font-size: 11px;
  align-self: flex-start;
  color: ${({ error }) => (error ? '#d0021b' : '#8a6d3b')};
`;

export const RightSender = styled.div`
  color: #0176ff;
  font-size: 12px;
  font-weight: bold;
  margin-left: auto;
`;

export const SystemSender = styled.div`
  font-size: 12px;
  font-weight: bold;
  margin-left: auto;
`;

export const RightChatMessage = styled.div`
  background: #f2f9ff;
  width: 80%;
  padding: 10px;
  border-radius: 5px;
  margin-left: auto;
  margin-bottom: 15px;
  white-space: pre-line;
`;

export const SystemMessage = styled.div`
  width: 80%;
  padding: 10px;
  border: 1px solid #e3e3e3;
  border-radius: 5px;
  margin-left: auto;
  margin-bottom: 15px;
  font-size: 14px;
  color: #565656;
  white-space: pre-line;
`;

export const Italic = styled.div`
  padding-top: 10px;
  font-style: italic;
`;

export const Date = styled.div`
  color: #555555;
  font-size: 12px;
  margin-left: calc(50% - 50px);
  position: absolute;
`;

export const ChatContainer = styled.div`
  width: 100%;
`;

export const MetaData = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
`;

export const ReplyButton = styled.button`
  background-color: #0176ff;
  color: white;
  font-size: 13px;
  padding: 3px 10px;
  border: 1px solid #0176ff;
  border-radius: 15px;
  width: 100px;
  display: flex;
  justify-content: center;
  gap: 5px;
  height: 28px;
  transition: all 0.3s ease-in;
  &:hover {
    color: #0176ff;
    font-weight: bold;
    cursor: pointer;
    background-color: white;
    border: 1px solid #0176ff;
  }
  &:disabled {
    background-color: white;
    color: grey;
    cursor: auto;
    border: 1px solid grey;
  }
`;

export const SendIcon = styled(Send)`
  transform: rotate(-35deg);
  padding-bottom: 4px;
`;

export const TextArea = styled.textarea`
  width: 100%;
  border: 1px solid #888;
  padding: 10px;
`;

export const ReplyText = styled.div`
  color: #0176ff;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 5px;
`;

export const ReplyTextContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
`;

export const ViewedUsers = styled.div`
  font-size: 12px;
  color: orange;
  display: flex;
  align-items: center;
`;

export const ViewedUserIcon = styled(Visibility)`
  transform: scale(0.7);
`;

export const ReplyActionArea = styled.div`
  display: flex;
  justify-content: space-between;
`;

// Keeps the attach button docked to the reply button instead of floating in the
// space left by `justify-content: space-between`.
export const ReplyActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const TextHelp = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const TextMetaData = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 10px;
  color: #555;
`;

export const ErrorMsg = styled.div`
  color: #ff6161;
  font-size: 12px;
  width: 100%;
  text-align: right;
`;
